import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { getAvailability, saveAvailability, adminSendReminders, getSchedulingStats } from "../../lib/api";
import { US_TIMEZONES, nextWallForWeekday, wallToUTCISO, fmtInTz } from "../../lib/timezones";

const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

export default function AdminAvailability({ adminKey }) {
  const [loading, setLoading] = useState(true);
  const [timezone, setTimezone] = useState("America/New_York");
  const [slots, setSlots] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [offsets, setOffsets] = useState([24]);
  const [busy, setBusy] = useState(false);
  const [awaiting, setAwaiting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAvailability(adminKey);
        setTimezone(data.timezone || "America/New_York");
        setSlots(Array.isArray(data.slots) ? data.slots : []);
        setBlackouts(Array.isArray(data.blackouts) ? data.blackouts : []);
        setOffsets(Array.isArray(data.reminder_offsets) && data.reminder_offsets.length ? data.reminder_offsets : [24]);
      } catch {
        toast.error("Could not load availability");
      } finally {
        setLoading(false);
      }
      try {
        const stats = await getSchedulingStats(adminKey);
        setAwaiting(stats.awaiting);
      } catch { /* non-blocking */ }
    })();
  }, [adminKey]);

  const addSlot = () => setSlots((s) => [...s, { weekday: 1, time: "10:00" }]);
  const setSlotAt = (i, patch) => setSlots((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));

  const addBlackout = () => setBlackouts((b) => [...b, ""]);
  const setBlackoutAt = (i, v) => setBlackouts((b) => b.map((x, idx) => (idx === i ? v : x)));
  const removeBlackout = (i) => setBlackouts((b) => b.filter((_, idx) => idx !== i));
  const toggleOffset = (h) => setOffsets((o) => (o.includes(h) ? o.filter((x) => x !== h) : [...o, h].sort((a, b) => b - a)));

  const save = async () => {
    setBusy(true);
    try {
      const clean = slots
        .filter((s) => s.time)
        .map((s) => ({ weekday: Number(s.weekday), time: s.time }));
      const cleanBlackouts = [...new Set(blackouts.filter(Boolean))].sort();
      const cleanOffsets = offsets.length ? offsets : [24];
      await saveAvailability({ timezone, slots: clean, blackouts: cleanBlackouts, reminder_offsets: cleanOffsets }, adminKey);
      setSlots(clean);
      setBlackouts(cleanBlackouts);
      toast.success("Availability saved", { description: "Use it with one click in any application." });
    } catch {
      toast.error("Could not save availability");
    } finally {
      setBusy(false);
    }
  };

  const runReminders = async () => {
    try {
      const res = await adminSendReminders(adminKey);
      toast.success("Reminders processed", { description: `${res.reminders_sent} reminder(s) sent for upcoming interviews.` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run reminders");
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>;
  }

  const blackoutList = blackouts.filter(Boolean);
  const preview = slots
    .filter((s) => s.time)
    .map((s) => {
      const wall = nextWallForWeekday(s.weekday, s.time, timezone, blackoutList);
      if (!wall) return null;
      const iso = wallToUTCISO(wall, timezone);
      return { iso, label: fmtInTz(iso, timezone) };
    })
    .filter(Boolean)
    .sort((a, b) => a.iso.localeCompare(b.iso));

  return (
    <div className="max-w-2xl" data-testid="admin-availability">
      <h2 className="font-heading text-2xl font-bold text-white">Recruiter Availability</h2>
      <p className="mt-2 text-sm text-slate-400">
        Set your default weekly interview times. When scheduling an interview, tap
        &ldquo;Use weekly availability&rdquo; to fill the next occurrence of each slot in one click.
      </p>

      <div className="mt-6">
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">Time zone</label>
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
          data-testid="availability-tz"
        >
          {US_TIMEZONES.map((t) => (
            <option key={t.value} value={t.value} className="bg-ink-900">{t.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 space-y-2">
        {slots.length === 0 && <p className="text-sm text-slate-500">No weekly slots yet. Add one below.</p>}
        {slots.map((s, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`availability-row-${i}`}>
            <select
              value={s.weekday}
              onChange={(e) => setSlotAt(i, { weekday: Number(e.target.value) })}
              className="flex-1 rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
              data-testid={`availability-weekday-${i}`}
            >
              {WEEKDAYS.map((d) => (
                <option key={d.value} value={d.value} className="bg-ink-900">{d.label}</option>
              ))}
            </select>
            <input
              type="time"
              value={s.time}
              onChange={(e) => setSlotAt(i, { time: e.target.value })}
              className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70 [color-scheme:dark]"
              data-testid={`availability-time-${i}`}
            />
            <button onClick={() => removeSlot(i)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:text-red-300" data-testid={`availability-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <button onClick={addSlot} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-sky-400 hover:text-sky-300" data-testid="availability-add">
        <Plus className="h-4 w-4" /> Add weekly slot
      </button>

      {preview.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/10 bg-ink-900/50 p-4" data-testid="availability-preview">
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate-400">Preview — next times these slots generate</p>
          <ul className="mt-3 space-y-1.5">
            {preview.map((p, i) => (
              <li key={`${i}-${p.iso}`} className="text-sm text-slate-200" data-testid="availability-preview-item">{p.label}</li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-t border-white/5 pt-3 text-sm" data-testid="availability-capacity">
            <span className="text-slate-300">
              Weekly capacity: <strong className="text-white">{preview.length}</strong> interview {preview.length === 1 ? "slot" : "slots"}
            </span>
            {awaiting != null && (
              <span className={awaiting > preview.length ? "text-amber-300" : "text-slate-300"}>
                Awaiting scheduling: <strong>{awaiting}</strong> candidate{awaiting === 1 ? "" : "s"}
                {awaiting > preview.length ? " — consider adding more slots" : ""}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Blackout dates */}
      <div className="mt-10">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Blackout dates</h3>
        <p className="mt-1 text-sm text-slate-500">Holidays or PTO — these dates are never proposed, even if a weekly slot lands on them.</p>
        <div className="mt-3 space-y-2">
          {blackouts.length === 0 && <p className="text-sm text-slate-500">No blackout dates.</p>}
          {blackouts.map((b, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`blackout-row-${i}`}>
              <input
                type="date"
                value={b}
                onChange={(e) => setBlackoutAt(i, e.target.value)}
                className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70 [color-scheme:dark]"
                data-testid={`blackout-date-${i}`}
              />
              <button onClick={() => removeBlackout(i)} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:text-red-300" data-testid={`blackout-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button onClick={addBlackout} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-sky-400 hover:text-sky-300" data-testid="blackout-add">
          <Plus className="h-4 w-4" /> Add blackout date
        </button>
      </div>

      {/* Reminder timing */}
      <div className="mt-10">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Reminder timing</h3>
        <p className="mt-1 text-sm text-slate-500">Automatically email candidates ahead of their interview. Pick one or more.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[{ h: 72, l: "3 days before" }, { h: 24, l: "1 day before" }, { h: 4, l: "4 hours before" }, { h: 2, l: "2 hours before" }, { h: 1, l: "1 hour before" }].map((o) => (
            <button
              key={o.h}
              onClick={() => toggleOffset(o.h)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${offsets.includes(o.h) ? "border-sky-400 bg-sky-400 text-ink-900" : "border-white/15 text-slate-300 hover:text-white"}`}
              data-testid={`reminder-offset-${o.h}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-2.5 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60" data-testid="availability-save">
          <Save className="h-4 w-4" /> {busy ? "Saving..." : "Save availability"}
        </button>
        <button onClick={runReminders} className="rounded-full border border-white/15 px-6 py-2.5 text-sm text-white hover:border-sky-400/60 hover:text-sky-300" data-testid="availability-run-reminders">
          Send due reminders now
        </button>
      </div>
    </div>
  );
}
